import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Icon } from "../../components/icons";
import { apiPost } from "../../api/client";
import { usePersona } from "../../hooks/usePersona";
import { useMe } from "../../hooks/useMe";
import { usePlannerStore } from "../../state/usePlannerStore";
import {
  useRoadmapEntries,
  useAssessEntry,
  useDeferEntry,
  useRequestContext,
  useReassignEntry,
  usePlanIt,
  useSetTemplateKeys,
  useSplitEntry,
  useSetRecurrence,
  type RoadmapEntry,
  type RoadmapFilter,
  type SplitChild,
  type RecurrenceFrequency,
  type RecurrenceConfig,
} from "../../hooks/useRoadmap";
import { useTemplates } from "../../hooks/useTemplates";

// The Inbox is a *decision queue* — entries that haven't yet been
// accepted into a play. Once planning starts (a draft play exists),
// the entry leaves the Inbox entirely and lives on the Plays page
// instead. Planning + Planned tabs therefore belong on Plays, not
// here. The Inbox carries only the tabs where a *planning decision*
// is needed.
const FILTERS: { id: RoadmapFilter; label: string }[] = [
  { id: "unplanned", label: "Unplanned" },
  { id: "deferred", label: "Deferred" },
  { id: "recurring", label: "Recurring" },
];

const dtf = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

function formatPromoted(iso: string): string {
  try {
    return dtf.format(new Date(iso));
  } catch {
    return iso;
  }
}

function pickString(v: unknown): string | null {
  if (typeof v !== "string" || v.length === 0) return null;
  if (v === "__unspecified__") return null;
  return v;
}

function pickNumber(v: unknown): number | null {
  return typeof v === "number" && isFinite(v) ? v : null;
}

function formatMetricNumber(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

interface ProjectedKpi {
  label: string;
  delta: string;
}

function readProjected(entry: RoadmapEntry): ProjectedKpi {
  const p = (entry.projected_outcome ?? {}) as Record<string, unknown>;
  // API sends: metric (string), baseline (number), target (number), current (number), unit (string)
  // Local entries may use: kpi (string), label (string), delta (string)
  const label =
    pickString(p["metric"]) ??
    pickString(p["kpi"]) ??
    pickString(p["label"]) ??
    "—";

  // Build delta: prefer current→target, fall back to baseline→target, then raw delta string.
  const current = pickNumber(p["current"]);
  const target = pickNumber(p["target"]);
  const baseline = pickNumber(p["baseline"]);
  const unit = pickString(p["unit"]) ?? "";
  const suffix = unit ? ` ${unit}` : "";

  let delta = "—";
  if (target !== null) {
    const from = current ?? baseline;
    if (from !== null) {
      delta = `${formatMetricNumber(from)}${suffix} → ${formatMetricNumber(target)}${suffix}`;
    } else {
      delta = `${formatMetricNumber(target)}${suffix}`;
    }
  } else {
    delta = pickString(p["delta"]) ?? "—";
  }

  return { label, delta };
}

function readArchetype(entry: RoadmapEntry): string {
  const lin = entry.signal_lineage ?? {};
  const tk = (lin as { template_keys?: { archetype?: string } }).template_keys;
  // Precedence: user-picked template archetype → XI-classified archetype.
  // The latter is set on federated-from-XTS rows by XTS's
  // src/services/initiative-metadata/classify-archetype.ts.
  const xtsArchetype = (lin as { xts_archetype?: string | null }).xts_archetype;
  return tk?.archetype ?? xtsArchetype ?? "—";
}

// Configurable per environment via Vite. The default points at
// xts.xamun.ai (the public XI/XTS instance) so isolated deployments can
// override without forking. Build-time env, so it's frozen into the
// bundle — set in deploy.env or Coolify env vars before `vite build`.
const _xtsBaseEnv = import.meta.env["VITE_XTS_BASE_URL"] as string | undefined;
if (!_xtsBaseEnv) {
  console.error("VITE_XTS_BASE_URL is not set — XTS links will be broken. Set this env var before building.");
}
const XTS_BASE = _xtsBaseEnv ?? "";

function xtsHrefFor(entry: RoadmapEntry): string | null {
  const lin = entry.signal_lineage ?? {};
  const sessionId = (lin as { roadmap_session_id?: unknown }).roadmap_session_id;
  if (typeof sessionId === "string" && sessionId.length > 0) {
    return `${XTS_BASE}/roadmap/${sessionId}`;
  }
  // Federated entry without session id — land on XTS roadmap list.
  if ((lin as { source?: unknown }).source === "xts") return `${XTS_BASE}/roadmap`;
  return null;
}

function isTemplateDirect(entry: RoadmapEntry): boolean {
  return (entry.signal_lineage as { source?: string } | null)?.source === "template_direct";
}

function readLineageSteps(entry: RoadmapEntry): string[] {
  const lin = entry.signal_lineage ?? {};
  const raw = (lin as { steps?: unknown }).steps;
  if (Array.isArray(raw)) {
    return raw.filter((s): s is string => typeof s === "string").slice(0, 3);
  }
  // Template-direct entries never had an XI signal — claiming "Signal →
  // XI Roadmap" lineage on those cards directly contradicts the ✦
  // Template pill rendered next to them. Use the catalog → template
  // pair instead so the row stays consistent.
  if (isTemplateDirect(entry)) {
    return ["Template catalog", "Use this template"];
  }
  return ["Signal", "XI Roadmap"];
}

interface ImpactReadout {
  label: "High" | "Medium" | "Low";
  pillClass: string;
}

function readImpact(entry: RoadmapEntry): ImpactReadout {
  const lin = entry.signal_lineage ?? {};
  const raw = (lin as { impact?: unknown }).impact;
  const v =
    typeof raw === "string" ? raw.toLowerCase() : null;
  if (v === "high") return { label: "High", pillClass: "pill green" };
  if (v === "low") return { label: "Low", pillClass: "pill gray" };
  return { label: "Medium", pillClass: "pill blue" };
}

function readCrossTeam(entry: RoadmapEntry): string | null {
  const lin = entry.signal_lineage ?? {};
  const v = (lin as { cross_team?: unknown }).cross_team;
  return typeof v === "string" && v.length > 0 ? v : null;
}

function readCohort(entry: RoadmapEntry): string | null {
  const lin = entry.signal_lineage ?? {};
  const v = (lin as { cohort?: unknown }).cohort;
  return typeof v === "string" && v.length > 0 ? v : null;
}

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return dtf.format(new Date(iso));
  } catch {
    return iso;
  }
}

function readTargetWindow(entry: RoadmapEntry): string {
  const s = fmtDate(entry.target_window_start);
  const e = fmtDate(entry.target_window_end);
  if (!s && !e) return "—";
  if (s && e) return `${s} → ${e}`;
  return s ?? e ?? "—";
}

/**
 * Compact pill rendering the AI Assessment recommendation. Lives in the
 * card's right rail next to impact/status. Hover surfaces the reasoning.
 */
function AssessmentPill({
  classification,
  entryId,
}: {
  classification: NonNullable<RoadmapEntry["ai_classification"]>;
  entryId: string;
}) {
  const label =
    classification.recommendation === "gameplan" ? "GamePlan" : "Single play";
  const pct = Math.round(classification.confidence * 100);
  return (
    <span
      className="pill"
      data-testid={`assessment-pill-${entryId}`}
      title={`${classification.reasoning} (model: ${classification.model}, ${classification.evaluated_by})`}
      style={{
        fontSize: 10,
        background: "color-mix(in srgb, var(--info, #1d4ed8) 12%, transparent)",
        color: "var(--info, #1d4ed8)",
      }}
    >
      ✦ Recommend: {label} · {pct}%
    </span>
  );
}

interface CardProps {
  entry: RoadmapEntry;
  workspaceId: string;
  canPlan: boolean;
  canOpenXI: boolean;
  canReassign: boolean;
  onPlanIt: (id: string) => void;
  onDefer: (id: string) => void;
  onRequestContext: (id: string) => void;
  onReassign: (id: string) => void;
  onOpenEditor: (id: string, title: string) => void;
  onSplit: (id: string) => void;
  onMakeRecurring: (id: string) => void;
  onAssess: (id: string) => void;
  assessBusy: boolean;
  busy: boolean;
  status: string | null;
}

function EntryCard({
  entry,
  workspaceId,
  canPlan,
  canOpenXI,
  canReassign,
  onPlanIt,
  onDefer,
  onRequestContext,
  onReassign,
  onOpenEditor,
  onSplit,
  onMakeRecurring,
  onAssess,
  assessBusy,
  busy,
  status,
}: CardProps) {
  const { label: kpiLabel, delta } = readProjected(entry);
  const archetype = readArchetype(entry);
  const lineage = readLineageSteps(entry);
  const window = readTargetWindow(entry);
  const impact = readImpact(entry);
  const crossTeam = readCrossTeam(entry);
  const cohort = readCohort(entry);
  return (
    <div className="entry" data-testid={`entry-${entry.id}`}>
      <div className="entry-l">
        <div className="eyebrow">
          <span>
            {isTemplateDirect(entry) ? "Created" : "Promoted"}{" "}
            {formatPromoted(entry.promoted_at)}
          </span>
          <span style={{ color: "var(--text-mute)" }}>·</span>
          <span>{isTemplateDirect(entry) ? "Template catalog" : "XI Roadmap"}</span>
          {crossTeam ? (
            <>
              <span style={{ color: "var(--text-mute)" }}>·</span>
              <span
                className="pill blue"
                data-testid={`cross-team-${entry.id}`}
                style={{ fontSize: 10 }}
              >
                Cross-team with {crossTeam}
              </span>
            </>
          ) : null}
        </div>
        <h3>{entry.title}</h3>
        <p className="intent">{entry.intent}</p>
        <div className="entry-meta">
          <div>
            Projected KPI
            <span className="v green">{kpiLabel}</span>
          </div>
          <div>
            Delta
            <span className="v green">{delta}</span>
          </div>
          <div>
            Target window
            <span className="v">{window}</span>
          </div>
          <div>
            Archetype
            <span className="v">{archetype}</span>
          </div>
        </div>
        <div className="lineage">
          <span className="h">
            {isTemplateDirect(entry) ? "Template lineage" : "Signal lineage"}
          </span>
          {lineage.map((step, i) => (
            <span key={`s-${i}`} className="step">
              {step}
              {i < lineage.length - 1 ? (
                <span className="arr" aria-hidden>
                  {" → "}
                </span>
              ) : null}
            </span>
          ))}
          {canOpenXI && xtsHrefFor(entry) !== null ? (
            <>
              <span className="arr" aria-hidden>
                →
              </span>
              <a
                href={xtsHrefFor(entry)!}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in XI ↗
              </a>
            </>
          ) : null}
        </div>
        {cohort ? (
          <div
            className="lineage"
            data-testid={`cohort-${entry.id}`}
            style={{ color: "var(--text-2)", fontSize: 11 }}
          >
            <span className="h">Cohort</span>
            <span className="step">{cohort}</span>
          </div>
        ) : null}
        {status ? (
          <div
            className="lineage"
            style={{ color: "var(--text-2)" }}
            data-testid={`status-${entry.id}`}
          >
            {status}
          </div>
        ) : null}
      </div>
      <div className="entry-r">
        <span
          className={impact.pillClass}
          data-testid={`impact-${entry.id}`}
          title={`Impact: ${impact.label}`}
        >
          {impact.label} impact
        </span>
        <span className="pill gray" style={{ fontSize: 10 }}>
          {entry.status}
        </span>
        {entry.entry_kind === "recurrence_template" && (
          <span className="pill" style={{ fontSize: 10, background: "var(--purple-tint, #f0eaff)", color: "var(--purple, #6b21a8)" }}>
            ↻ {(entry.recurrence as { frequency?: string } | null)?.frequency ?? "recurring"}
          </span>
        )}
        {(entry.signal_lineage as { split_into?: unknown[] } | null)?.split_into?.length ? (
          <span className="pill gray" style={{ fontSize: 10 }} title="This initiative was split into children">
            split
          </span>
        ) : null}
        {/* Source badge: distinguish template-direct entries (minted via
            "Use this template" from the catalog) from XI-promoted ones.
            XI entries are the default — only render the badge for
            non-XI sources to keep the row tidy. */}
        {(() => {
          const src = (entry.signal_lineage as { source?: string } | null)?.source;
          if (src === "template_direct") {
            return (
              <span
                className="pill"
                style={{
                  fontSize: 10,
                  background: "color-mix(in srgb, var(--accent, #6366f1) 12%, transparent)",
                  color: "var(--accent, #6366f1)",
                }}
                title="Created directly from a template — no XI signal lineage"
                data-testid={`source-pill-${entry.id}`}
              >
                ✦ Template
              </span>
            );
          }
          return null;
        })()}
        {entry.ai_classification ? (
          <AssessmentPill classification={entry.ai_classification} entryId={entry.id} />
        ) : null}
        <div className="entry-actions">
          {/* Primary action: View Play when one exists; Plan it only when
              the initiative is still uncommitted and has no active play. */}
          {canPlan && entry.active_play_id ? (
            <a
              href={`/workspaces/${workspaceId}/plays/${entry.active_play_id}`}
              className="btn primary"
              data-testid={`view-play-${entry.id}`}
              data-tip="Open the active play for this initiative"
            >
              View Play
            </a>
          ) : null}
          {canPlan && !entry.active_play_id && entry.status === "committed" ? (
            <button
              type="button"
              className="btn primary"
              disabled={busy}
              onClick={() => onPlanIt(entry.id)}
              data-testid={`plan-it-${entry.id}`}
              data-tip="Generate an AI-drafted execution play from your template library. Picks the best-matched template automatically."
            >
              <Icon name="sparkle" className="ic-svg sm" />
              {busy ? "Planning…" : "Plan it"}
            </button>
          ) : null}
          {/* Open in Editor only makes sense before a play exists */}
          {canPlan && !entry.active_play_id && entry.status === "committed" ? (
            <button
              type="button"
              className="btn"
              onClick={() => onOpenEditor(entry.id, entry.title)}
              data-testid={`open-editor-${entry.id}`}
              data-tip="Skip AI template matching — open the planner editor directly to build the step graph by hand"
            >
              Open in Editor
            </button>
          ) : null}
          {/* Split: available while the initiative is still committable,
              regardless of whether a draft play is already attached. */}
          {canPlan && entry.status === "committed" ? (
            <button
              type="button"
              className="btn"
              onClick={() => onSplit(entry.id)}
              data-testid={`split-${entry.id}`}
              data-tip="Break this initiative into two or more sibling initiatives — they're grouped into a GamePlan so they share execution context"
            >
              Convert to GamePlan
            </button>
          ) : null}
          {/* Make recurring: switch a one-shot entry into a recurrence
              template so the v1.5 scheduler will auto-spawn instances on
              cadence. Hidden once the entry is already a template — the
              badge shows current state and editing happens via PATCH. */}
          {canPlan && entry.entry_kind === "one_shot" && entry.status === "committed" ? (
            <button
              type="button"
              className="btn"
              onClick={() => onMakeRecurring(entry.id)}
              data-testid={`make-recurring-${entry.id}`}
              data-tip="Mark this initiative as recurring (monthly close, weekly review, etc.). Each cycle spawns its own play."
            >
              ↻ Make recurring
            </button>
          ) : null}
          {canPlan && entry.entry_kind === "recurrence_template" ? (
            <button
              type="button"
              className="btn"
              onClick={() => onMakeRecurring(entry.id)}
              data-testid={`edit-recurrence-${entry.id}`}
              data-tip="Change the recurrence schedule or revert to one-time."
            >
              Edit recurrence
            </button>
          ) : null}
          {canPlan && entry.status === "committed" ? (
            <button
              type="button"
              className="btn"
              onClick={() => onDefer(entry.id)}
              data-testid={`defer-${entry.id}`}
              data-tip="Park this initiative — XI is notified it's not the right moment. You can revisit it from the Deferred tab."
            >
              Defer
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            onClick={() => onRequestContext(entry.id)}
            data-testid={`request-context-${entry.id}`}
            data-tip="Ask XI for more signal before you plan — useful when the intent or projected outcome needs clarification"
          >
            More context
          </button>
          {/* AI Assessment trigger. Hidden for entries the classifier
              never touches (template-direct, recurrence templates,
              already-split-into-gameplan) — those would return 409. */}
          {canPlan &&
          entry.entry_kind === "one_shot" &&
          !isTemplateDirect(entry) &&
          !(entry.signal_lineage as { split_into?: unknown[] } | null)
            ?.split_into?.length ? (
            <button
              type="button"
              className="btn"
              onClick={() => onAssess(entry.id)}
              disabled={assessBusy}
              data-testid={`assess-${entry.id}`}
              data-tip="Run the AI Assessment classifier to recommend Play vs GamePlan for this initiative"
            >
              {assessBusy
                ? "Assessing…"
                : entry.ai_classification
                  ? "Re-assess with AI"
                  : "Assess with AI"}
            </button>
          ) : null}
          {canReassign ? (
            <button
              type="button"
              className="btn"
              onClick={() => onReassign(entry.id)}
              data-testid={`reassign-${entry.id}`}
              data-tip="Hand this initiative to a different CXO in your company"
            >
              Reassign
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface DeferModalProps {
  entryId: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (args: { reason: string; deferred_until?: string }) => void;
}

function DeferModal({
  entryId,
  pending,
  error,
  onClose,
  onSubmit,
}: DeferModalProps) {
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  return (
    <div
      className="entry"
      role="dialog"
      aria-label="Defer initiative"
      data-testid={`defer-modal-${entryId}`}
      style={{ borderColor: "var(--accent)" }}
    >
      <div className="entry-l" style={{ width: "100%" }}>
        <h3>Defer initiative</h3>
        <p className="intent">
          Park this initiative. XI is told it's not the right moment yet.
        </p>
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">Reason</span>
          <textarea
            data-testid="defer-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          />
        </label>
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">Defer until (optional)</span>
          <input
            type="date"
            data-testid="defer-until"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          />
        </label>
        {error ? (
          <div className="lineage" style={{ color: "crimson" }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="entry-r">
        <div className="entry-actions">
          <button
            type="button"
            className="btn primary"
            disabled={pending || reason.trim().length === 0}
            onClick={() =>
              onSubmit(
                until
                  ? { reason: reason.trim(), deferred_until: until }
                  : { reason: reason.trim() },
              )
            }
            data-testid="defer-submit"
          >
            {pending ? "Deferring…" : "Defer"}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface RecurrenceModalProps {
  entryId: string;
  entryTitle: string;
  current: RecurrenceConfig | null;
  isTemplate: boolean;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (recurrence: RecurrenceConfig) => void;
}

function RecurrenceModal({
  entryId,
  entryTitle,
  current,
  isTemplate,
  pending,
  error,
  onClose,
  onSubmit,
}: RecurrenceModalProps) {
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(
    current?.frequency ?? "monthly",
  );
  const [dayOfMonth, setDayOfMonth] = useState<string>(
    current?.day_of_month ? String(current.day_of_month) : "1",
  );
  const [dayOfWeek, setDayOfWeek] = useState<string>(
    current?.day_of_week !== undefined ? String(current.day_of_week) : "1",
  );
  const [startsOn, setStartsOn] = useState<string>(current?.starts_on ?? "");

  const showDayOfWeek = frequency === "weekly";
  const showDayOfMonth =
    frequency === "monthly" || frequency === "quarterly" || frequency === "annually";

  const handleSubmit = () => {
    const payload: RecurrenceConfig = { frequency };
    if (showDayOfMonth) {
      const n = Number(dayOfMonth);
      if (n >= 1 && n <= 28) payload.day_of_month = n;
    }
    if (showDayOfWeek) {
      const n = Number(dayOfWeek);
      if (n >= 0 && n <= 6) payload.day_of_week = n;
    }
    if (startsOn) payload.starts_on = startsOn;
    onSubmit(payload);
  };

  return (
    <div
      className="entry"
      role="dialog"
      aria-label={isTemplate ? "Edit recurrence" : "Make recurring"}
      data-testid={`recurrence-modal-${entryId}`}
      style={{ borderColor: "var(--accent)" }}
    >
      <div className="entry-l" style={{ width: "100%" }}>
        <h3>{isTemplate ? "Edit recurrence" : "Make recurring"}</h3>
        <p className="intent">
          {isTemplate
            ? `Adjust how often "${entryTitle}" spawns a new play.`
            : `Turn "${entryTitle}" into a recurring initiative. Each cycle will spawn its own play once the v1.5 scheduler is live; until then this marks the entry as a template so it doesn't show up in the unplanned tab.`}
        </p>
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">Frequency</span>
          <select
            data-testid="recurrence-frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </label>
        {showDayOfWeek ? (
          <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
            <span className="h">Day of week</span>
            <select
              data-testid="recurrence-day-of-week"
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 8,
                font: "inherit",
              }}
            >
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
              <option value="0">Sunday</option>
            </select>
          </label>
        ) : null}
        {showDayOfMonth ? (
          <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
            <span className="h">Day of month (1–28)</span>
            <input
              type="number"
              min={1}
              max={28}
              data-testid="recurrence-day-of-month"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 8,
                font: "inherit",
              }}
            />
          </label>
        ) : null}
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">Starts on (optional)</span>
          <input
            type="date"
            data-testid="recurrence-starts-on"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          />
        </label>
        {error ? (
          <div className="lineage" style={{ color: "crimson" }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="entry-r">
        <div className="entry-actions">
          <button
            type="button"
            className="btn primary"
            disabled={pending}
            onClick={handleSubmit}
            data-testid="recurrence-submit"
          >
            {pending ? "Saving…" : isTemplate ? "Save schedule" : "Make recurring"}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface RequestContextModalProps {
  entryId: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (args: { question: string }) => void;
}

function RequestContextModal({
  entryId,
  pending,
  error,
  onClose,
  onSubmit,
}: RequestContextModalProps) {
  const [question, setQuestion] = useState("");
  return (
    <div
      className="entry"
      role="dialog"
      aria-label="Request more context"
      data-testid={`request-context-modal-${entryId}`}
      style={{ borderColor: "var(--accent)" }}
    >
      <div className="entry-l" style={{ width: "100%" }}>
        <h3>Request more context</h3>
        <p className="intent">
          XI will reply with the additional signal you need before planning.
        </p>
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">Question</span>
          <textarea
            data-testid="request-context-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={4}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          />
        </label>
        {error ? (
          <div className="lineage" style={{ color: "crimson" }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="entry-r">
        <div className="entry-actions">
          <button
            type="button"
            className="btn primary"
            disabled={pending || question.trim().length === 0}
            onClick={() => onSubmit({ question: question.trim() })}
            data-testid="request-context-submit"
          >
            {pending ? "Sending…" : "Send"}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface ReassignModalProps {
  entryId: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (args: { new_owner_user_id: string; note?: string }) => void;
}

function ReassignModal({
  entryId,
  pending,
  error,
  onClose,
  onSubmit,
}: ReassignModalProps) {
  const [ownerId, setOwnerId] = useState("");
  const [note, setNote] = useState("");
  return (
    <div
      className="entry"
      role="dialog"
      aria-label="Reassign initiative"
      data-testid={`reassign-modal-${entryId}`}
      style={{ borderColor: "var(--accent)" }}
    >
      <div className="entry-l" style={{ width: "100%" }}>
        <h3>Reassign to another CXO</h3>
        <p className="intent">
          Hand this initiative to a different CXO in the company.
        </p>
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">New owner user id</span>
          <input
            data-testid="reassign-owner"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            placeholder="usr_…"
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          />
        </label>
        <label className="lineage" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span className="h">Note (optional)</span>
          <textarea
            data-testid="reassign-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          />
        </label>
        {error ? (
          <div className="lineage" style={{ color: "crimson" }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="entry-r">
        <div className="entry-actions">
          <button
            type="button"
            className="btn primary"
            disabled={pending || ownerId.trim().length === 0}
            onClick={() =>
              onSubmit(
                note.trim()
                  ? { new_owner_user_id: ownerId.trim(), note: note.trim() }
                  : { new_owner_user_id: ownerId.trim() },
              )
            }
            data-testid="reassign-submit"
          >
            {pending ? "Reassigning…" : "Reassign"}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

interface TemplateKeyPickerProps {
  entryId: string;
  entryTitle: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (keys: {
    industry: string;
    trade: string;
    archetype: string;
  }) => void;
  onSkipToDiscovery: () => void;
}

function TemplateKeyPicker({
  entryId,
  entryTitle,
  pending,
  error,
  onClose,
  onSubmit,
  onSkipToDiscovery,
}: TemplateKeyPickerProps) {
  const templates = useTemplates();
  const [industry, setIndustry] = useState("");
  const [trade, setTrade] = useState("");
  const [archetype, setArchetype] = useState("");

  const all = templates.data?.templates ?? [];
  const industries = useMemo(
    () => Array.from(new Set(all.map((t) => t.industry))).sort(),
    [all],
  );
  const trades = useMemo(
    () =>
      Array.from(
        new Set(all.filter((t) => t.industry === industry).map((t) => t.trade)),
      ).sort(),
    [all, industry],
  );
  const archetypes = useMemo(
    () =>
      Array.from(
        new Set(
          all
            .filter((t) => t.industry === industry && t.trade === trade)
            .map((t) => t.archetype),
        ),
      ).sort(),
    [all, industry, trade],
  );

  const ready =
    industry !== "" &&
    trade !== "" &&
    archetype !== "" &&
    all.some(
      (t) =>
        t.industry === industry &&
        t.trade === trade &&
        t.archetype === archetype,
    );

  return (
    <div
      className="entry"
      role="dialog"
      aria-label="Pick a template"
      data-testid={`template-keys-modal-${entryId}`}
      style={{ borderColor: "var(--accent)" }}
    >
      <div className="entry-l" style={{ width: "100%" }}>
        <h3>Pick a template</h3>
        <p className="intent">
          No template is attached to <strong>{entryTitle}</strong> yet. Pick
          an industry, trade, and archetype to use a templated plan, or skip
          to Discovery for a guided intake.
        </p>
        <label
          className="lineage"
          style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}
        >
          <span className="h">Industry</span>
          <select
            data-testid="template-keys-industry"
            value={industry}
            onChange={(e) => {
              setIndustry(e.target.value);
              setTrade("");
              setArchetype("");
            }}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          >
            <option value="">— select —</option>
            {industries.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label
          className="lineage"
          style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}
        >
          <span className="h">Trade</span>
          <select
            data-testid="template-keys-trade"
            value={trade}
            disabled={industry === ""}
            onChange={(e) => {
              setTrade(e.target.value);
              setArchetype("");
            }}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          >
            <option value="">— select —</option>
            {trades.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label
          className="lineage"
          style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}
        >
          <span className="h">Archetype</span>
          <select
            data-testid="template-keys-archetype"
            value={archetype}
            disabled={trade === ""}
            onChange={(e) => setArchetype(e.target.value)}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              font: "inherit",
            }}
          >
            <option value="">— select —</option>
            {archetypes.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        {error ? (
          <div className="lineage" style={{ color: "crimson" }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="entry-r">
        <div className="entry-actions">
          <button
            type="button"
            className="btn primary"
            disabled={pending || !ready}
            onClick={() => onSubmit({ industry, trade, archetype })}
            data-testid="template-keys-submit"
          >
            {pending ? "Planning…" : "Plan it"}
          </button>
          <button
            type="button"
            className="btn"
            disabled={pending}
            onClick={onSkipToDiscovery}
            data-testid="template-keys-skip"
          >
            Use Discovery instead
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard({ id }: { id: string }) {
  return (
    <div className="entry" data-testid={`skeleton-${id}`}>
      <div className="entry-l" style={{ width: "100%" }}>
        <div
          className="animate-pulse"
          style={{
            background: "var(--surface-2)",
            height: 12,
            width: 120,
            borderRadius: 4,
            marginBottom: 12,
          }}
        />
        <div
          className="animate-pulse"
          style={{
            background: "var(--surface-2)",
            height: 18,
            width: "60%",
            borderRadius: 4,
            marginBottom: 8,
          }}
        />
        <div
          className="animate-pulse"
          style={{
            background: "var(--surface-2)",
            height: 12,
            width: "80%",
            borderRadius: 4,
          }}
        />
      </div>
    </div>
  );
}

const FREQ_LABELS: Record<RecurrenceFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

interface SplitChildDraft {
  title: string;
  entry_kind: "one_shot" | "recurrence_template";
  frequency: RecurrenceFrequency;
}

function SplitModal({
  entryId,
  entryTitle,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  entryId: string;
  entryTitle: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (children: SplitChild[], rationale: string) => void;
}) {
  const [children, setChildren] = useState<SplitChildDraft[]>([
    { title: "", entry_kind: "one_shot", frequency: "monthly" },
    { title: "", entry_kind: "one_shot", frequency: "monthly" },
  ]);
  const [rationale, setRationale] = useState("");

  const update = (i: number, patch: Partial<SplitChildDraft>) =>
    setChildren((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const canSubmit = children.every((c) => c.title.trim().length > 0);

  const handleSubmit = () => {
    const mapped: SplitChild[] = children.map((c) => ({
      title: c.title.trim(),
      entry_kind: c.entry_kind,
      ...(c.entry_kind === "recurrence_template"
        ? { recurrence: { frequency: c.frequency } }
        : {}),
    }));
    onSubmit(mapped, rationale.trim());
  };

  const inputStyle = {
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 10px",
    font: "inherit",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box" as const,
  };

  return (
    <div
      className="entry"
      role="dialog"
      aria-label="Convert to GamePlan"
      data-testid={`split-modal-${entryId}`}
      style={{ borderColor: "var(--accent)" }}
    >
      <div className="entry-l" style={{ width: "100%" }}>
        <h3 style={{ marginBottom: 4 }}>Convert to GamePlan</h3>
        <p className="intent" style={{ marginBottom: 16 }}>
          Breaking <strong>{entryTitle}</strong> into sibling initiatives. They&apos;ll
          be grouped into a GamePlan so their resulting plays share execution
          context and can be tracked as one campaign.
        </p>

        {children.map((c, i) => (
          <div
            key={i}
            style={{
              border: "1px solid var(--border)",
              borderRadius: 10,
              padding: 12,
              marginBottom: 10,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", flex: 1 }}>
                INITIATIVE {i + 1}
              </span>
              {children.length > 2 && (
                <button
                  type="button"
                  className="btn"
                  style={{ fontSize: 11, padding: "2px 8px" }}
                  onClick={() => setChildren((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  Remove
                </button>
              )}
            </div>
            <input
              type="text"
              placeholder="Initiative title…"
              value={c.title}
              onChange={(e) => update(i, { title: e.target.value })}
              style={inputStyle}
              data-testid={`split-child-title-${i}`}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select
                value={c.entry_kind}
                onChange={(e) =>
                  update(i, { entry_kind: e.target.value as SplitChildDraft["entry_kind"] })
                }
                style={{ ...inputStyle, width: "auto" }}
                data-testid={`split-child-kind-${i}`}
              >
                <option value="one_shot">One-time</option>
                <option value="recurrence_template">Recurring</option>
              </select>
              {c.entry_kind === "recurrence_template" && (
                <select
                  value={c.frequency}
                  onChange={(e) => update(i, { frequency: e.target.value as RecurrenceFrequency })}
                  style={{ ...inputStyle, width: "auto" }}
                  data-testid={`split-child-freq-${i}`}
                >
                  {(Object.keys(FREQ_LABELS) as RecurrenceFrequency[]).map((f) => (
                    <option key={f} value={f}>{FREQ_LABELS[f]}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        ))}

        {children.length < 6 && (
          <button
            type="button"
            className="btn"
            style={{ marginBottom: 12, fontSize: 12 }}
            onClick={() =>
              setChildren((prev) => [
                ...prev,
                { title: "", entry_kind: "one_shot", frequency: "monthly" },
              ])
            }
          >
            + Add another
          </button>
        )}

        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", display: "block", marginBottom: 4 }}>
            RATIONALE (optional — stored in governance trail)
          </span>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Why is this being split?"
            rows={2}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </label>

        {error && (
          <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 8 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn primary"
            disabled={pending || !canSubmit}
            onClick={handleSubmit}
            data-testid="split-submit"
          >
            {pending ? "Converting…" : "Create GamePlan"}
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

type ModalState =
  | { kind: "none" }
  | { kind: "defer"; entryId: string }
  | { kind: "request-context"; entryId: string }
  | { kind: "reassign"; entryId: string }
  | { kind: "template-keys"; entryId: string }
  | { kind: "split"; entryId: string }
  | { kind: "recurrence"; entryId: string };

export function RoadmapInbox() {
  const params = useParams<{ workspaceId: string }>();
  const workspaceId = params.workspaceId ?? "";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?with_template=<key> arrives from Templates catalog → "Use this
  // template" so the inbox can surface a hint banner ("Template X is
  // selected — click Plan it on an entry"). The override is also kept
  // in the planner store via the catalog handler so PlanIt picks it up.
  const carriedTemplateKey = searchParams.get("with_template") ?? null;
  const persona = usePersona(workspaceId);
  const [filter, setFilter] = useState<RoadmapFilter>("unplanned");
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  const [statusByEntry, setStatusByEntry] = useState<Record<string, string>>(
    {},
  );
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const me = useMe();
  const activeDomain = useMemo(() => {
    const m = me.data?.memberships.find((x) => x.workspaceId === workspaceId);
    return m?.defaultDomain ?? null;
  }, [me.data, workspaceId]);
  const query = useRoadmapEntries(workspaceId, filter, activeDomain);
  const planIt = usePlanIt(workspaceId);
  const defer = useDeferEntry(workspaceId);
  const requestContext = useRequestContext(workspaceId);
  const reassign = useReassignEntry(workspaceId);
  const setTemplateKeys = useSetTemplateKeys(workspaceId);
  const splitEntry = useSplitEntry(workspaceId);
  const setRecurrence = useSetRecurrence(workspaceId);
  const assessEntry = useAssessEntry(workspaceId);

  const handleSetRecurrence = (
    entryId: string,
    recurrence: RecurrenceConfig,
  ) => {
    setRecurrence.mutate(
      { entryId, recurrence },
      { onSuccess: () => setModal({ kind: "none" }) },
    );
  };
  const plannerStore = usePlannerStore();

  const canPlan =
    persona === "cxo" || persona === "operator" || persona === "owner";
  const canOpenXI = persona === "cxo";
  const canReassign = persona === "cxo";

  const entries = useMemo(
    () => query.data?.entries ?? [],
    [query.data?.entries],
  );

  const setStatus = (id: string, msg: string | null) => {
    setStatusByEntry((s) => {
      if (msg === null) {
        const next = { ...s };
        delete next[id];
        return next;
      }
      return { ...s, [id]: msg };
    });
  };

  const handleOpenEditor = (entryId: string, title: string) => {
    // Skip Discovery: patch the planner store so when the planner mounts
    // it reads stage=editor and falls back to the design fixture
    // (Wholesale Margin Recovery — 13 steps, 5 phases, swim lanes,
    // agent bindings). usePlannerStore.getState().patch updates the
    // in-memory store AND lets the zustand persist middleware write
    // through to localStorage in one shot — writing localStorage
    // directly doesn't take effect until next app reload.
    plannerStore.patch(entryId || "__default__", {
      stage: "editor",
      editorView: "timeline",
      showDeps: true,
      selectedStepId: null,
    });
    const search = new URLSearchParams({
      roadmapEntryId: entryId,
      title,
    }).toString();
    navigate(`/workspaces/${workspaceId}/planner?${search}`);
  };

  const fallbackToDiscovery = async (entryId: string) => {
    setStatus(entryId, "Starting Discovery session…");
    try {
      const resp = await apiPost<{ session: { id: string } }>(
        `/api/workspaces/${workspaceId}/discovery/sessions`,
        { roadmap_entry_id: entryId, mode: "chat" },
      );
      if (!resp.session?.id) {
        if (isMounted.current)
          setStatus(
            entryId,
            "Discovery session created without an id — please retry.",
          );
        return;
      }
      if (isMounted.current) setStatus(entryId, null);
      navigate(`/workspaces/${workspaceId}/discovery/${resp.session.id}`);
    } catch (e) {
      if (isMounted.current)
        setStatus(entryId, `Could not start Discovery: ${(e as Error).message}`);
    }
  };

  const handlePlanIt = async (entryId: string) => {
    setStatus(entryId, "Planning…");
    try {
      const result = await planIt.mutateAsync({ entryId });
      if (result.kind === "draft_play" && result.play.id) {
        if (isMounted.current) setStatus(entryId, null);
        navigate(`/workspaces/${workspaceId}/plays/${result.play.id}`);
        return;
      }
      if (result.kind === "discovery_required") {
        // Only `no_template_match` is fixable from the inbox — the CXO
        // picks the (industry, trade, archetype) tuple and we retry the
        // decompose. Other reasons (missing_params, capability_gap) still
        // need Discovery's structured intake.
        if (result.reason === "no_template_match") {
          if (isMounted.current) {
            setStatus(entryId, null);
            setModal({ kind: "template-keys", entryId });
          }
          return;
        }
        await fallbackToDiscovery(entryId);
        return;
      }
    } catch (e) {
      if (isMounted.current)
        setStatus(entryId, `Plan-it failed: ${(e as Error).message}`);
    }
  };

  const handleTemplateKeysSubmit = async (
    entryId: string,
    keys: { industry: string; trade: string; archetype: string },
  ) => {
    setStatus(entryId, "Saving template…");
    try {
      await setTemplateKeys.mutateAsync({ entryId, ...keys });
      setModal({ kind: "none" });
      // Retry Plan-It now that template_keys are set. If decompose still
      // says discovery_required (e.g. missing_params on the matched
      // template), the standard fallback kicks in.
      await handlePlanIt(entryId);
    } catch (e) {
      setStatus(entryId, `Could not save template: ${(e as Error).message}`);
    }
  };

  const handleDeferSubmit = async (
    entryId: string,
    args: { reason: string; deferred_until?: string },
  ) => {
    try {
      await defer.mutateAsync({ entryId, ...args });
      setStatus(entryId, "Deferred.");
      setModal({ kind: "none" });
    } catch (e) {
      setStatus(entryId, `Defer failed: ${(e as Error).message}`);
    }
  };

  const handleRequestContextSubmit = async (
    entryId: string,
    args: { question: string },
  ) => {
    try {
      await requestContext.mutateAsync({ entryId, ...args });
      setStatus(entryId, "Question sent to XI.");
      setModal({ kind: "none" });
    } catch (e) {
      setStatus(entryId, `Request failed: ${(e as Error).message}`);
    }
  };

  const handleReassignSubmit = async (
    entryId: string,
    args: { new_owner_user_id: string; note?: string },
  ) => {
    try {
      await reassign.mutateAsync({ entryId, ...args });
      setStatus(entryId, "Reassigned.");
      setModal({ kind: "none" });
    } catch (e) {
      setStatus(entryId, `Reassign failed: ${(e as Error).message}`);
    }
  };

  const handleSplitSubmit = async (
    entryId: string,
    children: SplitChild[],
    rationale: string,
  ) => {
    try {
      await splitEntry.mutateAsync({ entryId, children, ...(rationale ? { rationale } : {}) });
      setModal({ kind: "none" });
      setStatus(entryId, `Split into ${children.length} initiatives.`);
    } catch (e) {
      setStatus(entryId, `Split failed: ${(e as Error).message}`);
    }
  };

  // Source breakdown from the loaded page — the API doesn't return
  // per-source totals so this is approximate when paginated, but
  // exact when total fits on one page (the common case). The
  // disambiguation matters because "X promoted from XI" was lying
  // when local template plays accumulated alongside XI initiatives.
  const sourceCounts = (() => {
    const entries = query.data?.entries ?? [];
    let templateDirect = 0;
    for (const e of entries) {
      const src = (e.signal_lineage as { source?: string } | null)?.source;
      if (src === "template_direct") templateDirect++;
    }
    return { templateDirect, xi: entries.length - templateDirect };
  })();
  const totalLabel = (() => {
    if (query.data === undefined) return "Loading…";
    const total = query.data.total;
    const pluralize = (n: number, one: string, many: string) =>
      `${n} ${n === 1 ? one : many}`;
    if (sourceCounts.templateDirect === 0) {
      return pluralize(total, "initiative from XI", "initiatives from XI");
    }
    if (sourceCounts.xi === 0) {
      return pluralize(total, "template-direct entry", "template-direct entries");
    }
    return `${total} entries — ${sourceCounts.xi} from XI, ${sourceCounts.templateDirect} from templates`;
  })();

  return (
    <div className="main-inner" data-testid="roadmap-inbox">
      <div className="page-h">
        <span className="ttl">Roadmap inbox</span>
        <span className="sub">{totalLabel}</span>
      </div>
      <div className="page-sub">
        XI-promoted initiatives and locally-created template plays both land
        here. Plan, defer, or request more context. New plays start with
        Plan-it — no orphan plays. Entries with the ✦ Template pill were
        created from the catalog, not from an XI signal.
      </div>
      {carriedTemplateKey ? (
        <div
          data-testid="inbox-template-hint"
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            borderRadius: 8,
            background: "color-mix(in srgb, var(--accent, #6366f1) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--accent, #6366f1) 30%, transparent)",
            fontSize: 12,
            color: "var(--text-2)",
            display: "flex",
            gap: 12,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 16 }}>✦</span>
          <span style={{ flex: 1 }}>
            Template <strong style={{ color: "var(--text-1)" }}>{carriedTemplateKey}</strong> is selected.
            Click <em>Plan it</em> on any initiative below to commit it with this template.
          </span>
        </div>
      ) : null}
      <div className="inbox-toolbar">
        <div className="seg" role="tablist" aria-label="Filter inbox">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={filter === f.id ? "active" : ""}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="count">Sorted by promoted-at, newest first</div>
        <div className="right">
          <button type="button" className="btn">
            <Icon name="filter" className="ic-svg sm" />
            Filter
          </button>
        </div>
      </div>

      {query.isLoading ? (
        <>
          <SkeletonCard id="1" />
          <SkeletonCard id="2" />
          <SkeletonCard id="3" />
        </>
      ) : query.isError ? (
        <div className="entry" data-testid="roadmap-error">
          <div className="entry-l">
            <h3>Couldn't load the roadmap inbox.</h3>
            <p className="intent">{query.error?.message ?? "Unknown error"}</p>
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="entry" data-testid="roadmap-empty">
          <div className="entry-l">
            <h3>
              {filter === "unplanned"
                ? "Inbox zero."
                : filter === "deferred"
                  ? "No deferred initiatives."
                  : "No recurring initiatives yet."}
            </h3>
            <p className="intent">
              {filter === "unplanned"
                ? "No new initiatives waiting to be planned. They'll appear here when XI promotes a roadmap entry or you create one from a template. Plays already in flight live on the Plays page."
                : filter === "deferred"
                  ? "Initiatives you defer land here so you can revisit them later."
                  : "Recurring entries appear here once XI starts spawning them. The XI-side scheduler ships in v1.5 release 2 — until then, monthly close / quarterly board prep / weekly pipeline review and other BAU plays land in this tab automatically."}
            </p>
          </div>
        </div>
      ) : (
        entries.map((entry) => (
          <div key={entry.id}>
            <EntryCard
              entry={entry}
              workspaceId={workspaceId ?? ""}
              canPlan={canPlan}
              canOpenXI={canOpenXI}
              canReassign={canReassign}
              busy={planIt.isPending && planIt.variables?.entryId === entry.id}
              status={statusByEntry[entry.id] ?? null}
              onPlanIt={handlePlanIt}
              onDefer={(id) => setModal({ kind: "defer", entryId: id })}
              onRequestContext={(id) =>
                setModal({ kind: "request-context", entryId: id })
              }
              onReassign={(id) =>
                setModal({ kind: "reassign", entryId: id })
              }
              onOpenEditor={handleOpenEditor}
              onSplit={(id) => setModal({ kind: "split", entryId: id })}
              onMakeRecurring={(id) => setModal({ kind: "recurrence", entryId: id })}
              onAssess={(id) => assessEntry.mutate(id)}
              assessBusy={
                assessEntry.isPending && assessEntry.variables === entry.id
              }
            />
            {modal.kind === "recurrence" && modal.entryId === entry.id ? (
              <RecurrenceModal
                entryId={entry.id}
                entryTitle={entry.title}
                current={(entry.recurrence as RecurrenceConfig | null) ?? null}
                isTemplate={entry.entry_kind === "recurrence_template"}
                pending={setRecurrence.isPending}
                error={setRecurrence.error?.message ?? null}
                onClose={() => setModal({ kind: "none" })}
                onSubmit={(recurrence) =>
                  handleSetRecurrence(entry.id, recurrence)
                }
              />
            ) : null}
            {modal.kind === "split" && modal.entryId === entry.id ? (
              <SplitModal
                entryId={entry.id}
                entryTitle={entry.title}
                pending={splitEntry.isPending}
                error={splitEntry.error?.message ?? null}
                onClose={() => setModal({ kind: "none" })}
                onSubmit={(children, rationale) =>
                  handleSplitSubmit(entry.id, children, rationale)
                }
              />
            ) : null}
            {modal.kind === "defer" && modal.entryId === entry.id ? (
              <DeferModal
                entryId={entry.id}
                pending={defer.isPending}
                error={defer.error?.message ?? null}
                onClose={() => setModal({ kind: "none" })}
                onSubmit={(args) => handleDeferSubmit(entry.id, args)}
              />
            ) : null}
            {modal.kind === "request-context" && modal.entryId === entry.id ? (
              <RequestContextModal
                entryId={entry.id}
                pending={requestContext.isPending}
                error={requestContext.error?.message ?? null}
                onClose={() => setModal({ kind: "none" })}
                onSubmit={(args) =>
                  handleRequestContextSubmit(entry.id, args)
                }
              />
            ) : null}
            {modal.kind === "reassign" && modal.entryId === entry.id ? (
              <ReassignModal
                entryId={entry.id}
                pending={reassign.isPending}
                error={reassign.error?.message ?? null}
                onClose={() => setModal({ kind: "none" })}
                onSubmit={(args) => handleReassignSubmit(entry.id, args)}
              />
            ) : null}
            {modal.kind === "template-keys" && modal.entryId === entry.id ? (
              <TemplateKeyPicker
                entryId={entry.id}
                entryTitle={entry.title}
                pending={setTemplateKeys.isPending || planIt.isPending}
                error={setTemplateKeys.error?.message ?? null}
                onClose={() => setModal({ kind: "none" })}
                onSubmit={(keys) =>
                  handleTemplateKeysSubmit(entry.id, keys)
                }
                onSkipToDiscovery={() => {
                  setModal({ kind: "none" });
                  void fallbackToDiscovery(entry.id);
                }}
              />
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}
